
import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

interface HeroImage25DProps {
    src: string;
    alt: string;
    className?: string;
}

const HeroImage25D: React.FC<HeroImage25DProps> = ({ src, alt, className = "" }) => {
    const ref = useRef<HTMLDivElement>(null);

    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseXSpring = useSpring(x);
    const mouseYSpring = useSpring(y);

    const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["15deg", "-15deg"]);
    const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-15deg", "15deg"]);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!ref.current) return;

        const rect = ref.current.getBoundingClientRect();

        const width = rect.width;
        const height = rect.height;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const xPct = mouseX / width - 0.5;
        const yPct = mouseY / height - 0.5;

        x.set(xPct);
        y.set(yPct);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
                rotateY,
                rotateX,
                transformStyle: "preserve-3d",
            }}
            animate={{
                y: [0, -15, 0],
            }}
            transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
            }}
            className={`relative transition-all duration-200 ease-out ${className}`}
        >
            <div
                style={{
                    transform: "translateZ(75px)",
                    transformStyle: "preserve-3d",
                }}
                className="absolute inset-0 bg-transparent rounded-xl shadow-2xl"
            >
                {/* Optional: Add a shadow or background element here if needed */}
            </div>
            <motion.div
                style={{
                    transform: "translateZ(50px)",
                    transformStyle: "preserve-3d",
                }}
                className="relative z-10 block rounded-xl overflow-hidden shadow-xl"
            >
                <img
                    src={src}
                    alt={alt}
                    className="w-full h-full object-cover rounded-xl"
                />
                <motion.div
                    style={{
                        background: "radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)",
                        opacity: 0.5,
                        pointerEvents: 'none',
                        mixBlendMode: 'overlay'
                    }}
                    animate={{
                        x: ['-50%', '150%'],
                        y: ['-50%', '150%']
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatDelay: 2
                    }}
                    className="absolute inset-0 z-20 w-[200%] h-[200%] transform -rotate-45"
                />
            </motion.div>
        </motion.div>
    );
};

export default HeroImage25D;
